// ─────────────────────────────────────────────
// KLOTHIQ — Edit Variant Modal Component
// Allows editing size, color, SKU, price, cost & alert limits
// ─────────────────────────────────────────────

import UserGuide from '@/components/help/UserGuide';
import { Input } from '@/components/ui/FormControls';
import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, X, Pencil } from 'lucide-react';
import { useProduct, type ProductVariant, type ProductWithVariants } from '@/hooks/useProducts';
import { useOperation, useOperations } from '@/hooks/useOperations';
import { useOperationIntent } from '@/hooks/useOperationIntent';
import { useJerseyEntry, useJerseyEntrySave } from '@/hooks/useJerseyEntry';

export interface EditVariantModalProps {
  isOpen: boolean;
  onClose: () => void;
  variant: ProductVariant | null;
}

export default function EditVariantModal({ isOpen, onClose, variant }: EditVariantModalProps) {
  const queryClient = useQueryClient();
  const parent = useProduct(variant?.product_id);
  const operations = useOperations();
  const guardedSave = useOperation('save_product_workspace_v1');
  const intent = useOperationIntent(`edit-variant:${variant?.id || ''}`);
  const entry = useJerseyEntry(parent.data?.product_type_id || undefined, variant?.product_id);
  const entrySave = useJerseyEntrySave();

  const [sku, setSku] = useState('');
  const [size, setSize] = useState('');
  const [color, setColor] = useState('');
  const [cost, setCost] = useState('');
  const [price, setPrice] = useState('');
  const [lowStock, setLowStock] = useState('5');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const saving = useRef(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (variant) {
      setSku(variant.sku ?? '');
      setSize(variant.size ?? '');
      setColor(variant.color ?? '');
      setCost(variant.default_cost ? Number(variant.default_cost).toString() : '0');
      setPrice(variant.default_price ? Number(variant.default_price).toString() : '0');
      setLowStock(String(variant.low_stock_level ?? 5));
    }
  }, [variant]);

  if (!isOpen || !variant) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving.current) return;
    if (!sku.trim() || !size.trim()) {
      toast.error('SKU and Size are required');
      return;
    }

    saving.current = true;
    setIsSubmitting(true);
    setSaveError('');
    try {
      if (operations.isLoading || parent.isLoading || entry.isFetching)
        throw new Error('Product details are still loading. Try again.');
      if (operations.error || parent.error || entry.error)
        throw new Error('Product settings are unavailable. Retry before saving.');
      if (entry.data?.entry_config?.enabled && !entry.data.enabled)
        throw new Error('Optional jersey entry is temporarily unavailable. Retry later.');
      if (entry.data?.enabled && parent.data) {
        const p = parent.data as ProductWithVariants & { schema_version_id?: string | null };
        await entrySave.mutateAsync({
          request_key: intent.key('save'),
          product_id: p.id,
          expected_updated_at: p.updated_at,
          product_type_id: p.product_type_id,
          schema_version_id: p.schema_version_id ?? null,
          configuration_version_id: entry.data.configuration_version_id,
          recipe_version: 2,
          name: { mode: 'manual', manual_value: p.name },
          code: { mode: 'retained', value: p.product_code },
          category_id: p.category_id,
          target_audience_id: p.target_audience_id,
          age_group_id: p.age_group_id,
          description: p.description,
          attributes: p.attributes,
          details: {},
          variants: [
            {
              client_key: variant.id,
              id: variant.id,
              sku:
                sku === variant.sku
                  ? { mode: 'retained', value: variant.sku }
                  : { mode: 'manual', manual_value: sku },
              size: size.trim(),
              colour: color.trim() || null,
              default_cost: Number(cost),
              default_price: Number(price),
              low_stock_level: lowStock.trim() === '' ? 5 : Number(lowStock),
              opening_stock: 0,
              attributes: variant.attributes || {},
            },
          ],
        });
        intent.complete('save');
      } else if (operations.data?.modules.catalog === 1) {
        const p = parent.data as
          | (ProductWithVariants & { schema_version_id?: string | null })
          | undefined;
        if (!p) throw new Error('Reload the product before editing this variant.');
        await guardedSave.mutateAsync({
          request_key: intent.key('save'),
          product_id: p.id,
          expected_updated_at: p.updated_at,
          product_type_id: p.product_type_id,
          schema_version_id: p.schema_version_id || null,
          product_code: p.product_code,
          name: p.name,
          category_id: p.category_id,
          target_audience_id: p.target_audience_id,
          age_group_id: p.age_group_id,
          description: p.description,
          attributes: p.attributes,
          variants: [
            {
              id: variant.id,
              sku: variant.sku,
              size: size.trim(),
              color: color.trim() || null,
              default_cost: Number(cost),
              default_price: Number(price),
              low_stock_level: lowStock.trim() === '' ? 5 : Number(lowStock),
              stock_quantity: 0,
              attributes: variant.attributes || {},
            },
          ],
        });
        intent.complete('save');
      } else {
        const { error } = await supabase
          .from('product_variants')
          .update({
            sku: variant.sku,
            size: size.trim(),
            color: color.trim() || null,
            default_cost: parseFloat(cost) || 0,
            default_price: parseFloat(price) || 0,
            low_stock_level: lowStock.trim() === '' ? 5 : Number(lowStock),
            updated_at: new Date().toISOString(),
          })
          .eq('id', variant.id);

        if (error) throw error;
      }

      toast.success(`Variant ${sku.toUpperCase()} updated successfully`);
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      onClose();
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'The save could not be confirmed.');
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to update variant');
    } finally {
      saving.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      data-field-guide-scope
    >
      <div className="obsidian-panel w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between border-b border-border pb-3">
          <div className="flex items-center gap-2">
            <Pencil className="h-5 w-5 text-primary" />
            <div className="flex items-start justify-between gap-3 pr-8">
              <h3 className="text-base font-semibold text-foreground">
                Edit Variant — {variant.sku}
              </h3>
              <UserGuide compact />
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {saveError && (
            <div role="alert" className="space-y-1 text-xs text-destructive">
              <p>{saveError}</p>
              <button
                type="button"
                className="min-h-11 underline"
                onClick={() =>
                  void intent
                    .recover('save')
                    .then(async (result) => {
                      if (!result) {
                        toast.info('No completed save found. Keep the same details when retrying.');
                        return;
                      }
                      await queryClient.invalidateQueries({ queryKey: ['products'] });
                      await queryClient.invalidateQueries({ queryKey: ['inventory'] });
                      toast.success('The previous variant edit was recovered.');
                      onClose();
                    })
                    .catch((e) => toast.error(e.message))
                }
              >
                Recover previous edit
              </button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">SKU *</label>
              <Input
                type="text"
                value={sku}
                readOnly={!entry.data?.enabled}
                onChange={(e) => setSku(e.target.value)}
                required
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm font-mono uppercase focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                helpLabel="SKU *"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Size *</label>
              <Input
                type="text"
                value={size}
                onChange={(e) => setSize(e.target.value)}
                required
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                helpLabel="Size *"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Color / Option</label>
            <Input
              type="text"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              placeholder="e.g. Blue / White"
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              helpLabel="Color / Option"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Cost (₹)</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                helpLabel="Cost (₹)"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Sell Price (₹)</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                helpLabel="Sell Price (₹)"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Low Stock Alert Threshold (Alert when stock &lt;)
            </label>
            <Input
              type="number"
              min="0"
              value={lowStock}
              onChange={(e) => setLowStock(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              helpLabel="Low Stock Alert Threshold (Alert when stock &lt;)"
            />
          </div>

          <div className="mt-6 flex justify-end gap-2 border-t border-border pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border bg-background px-4 py-2 text-xs font-medium text-foreground hover:bg-accent"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || parent.isLoading || entry.isFetching || !!entry.error}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {isSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
